import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { FaChevronDown, FaChevronUp, FaFilter, FaSearch, FaSort, FaTimes } from 'react-icons/fa';
import { ShoppingCart } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import FavoriteButton from "./FavoriteButton";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME (match CategoryPage) ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#fff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* ================= LOADER ================= */
const spin = keyframes`to { transform: rotate(360deg); }`;
const PageLoader = styled.div`display:grid;place-items:center;min-height:60vh;`;
const Spinner = styled.div`
  width:42px;height:42px;border-radius:50%;
  border:4px solid rgba(0,0,0,.1);border-top-color:${COLORS.text};
  animation:${spin} 1s linear infinite;
`;

/* ================= LAYOUT ================= */
const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 12px clamp(10px,4vw,16px) 28px;
  overflow-x: hidden !important;
`;
const GridWrap = styled(Container)`
  padding-top: 18px;
  padding-left: 0; padding-right: 0;
  @media (min-width: 641px){ padding-left:16px; padding-right:16px; }
`;

/* ================= TOP BAR ================= */
const TopBar = styled.div`
  position: sticky;
  top: 66px;
  z-index: 5;
  background: rgba(255,255,255,.85);
  backdrop-filter: saturate(180%) blur(8px);
  border-bottom: 1px solid ${COLORS.line};
`;
const TopBarInner = styled(Container)`
  display:flex;align-items:center;justify-content:space-between;gap:10px;
  padding-top:10px;padding-bottom:10px;
  overflow: visible !important;
`;
const LeftActions = styled.div`display:flex;gap:10px;flex-wrap:wrap;`;
const RightActions = styled.div`position:relative;`;

const PillButton = styled.button`
  appearance:none;border:1px solid ${COLORS.line};
  background:#fff;color:${COLORS.text};
  border-radius:999px;height:40px;padding:0 14px;
  display:inline-flex;align-items:center;gap:8px;
  font-weight:700;cursor:pointer;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
  &:hover{ box-shadow:${COLORS.shadow}; transform:translateY(-1px); }
`;

const SortDropdownWrap = styled.div`position:relative;`;
const DropdownCard = styled.div`
  position:absolute;top:calc(100% + 8px);right:0;
  min-width:220px;background:#fff;border:1px solid ${COLORS.line};
  border-radius:14px;box-shadow:${COLORS.shadow};padding:8px;z-index:20;
  ${({$hidden}) => $hidden && css`display:none;`}
`;
const DropdownItem = styled.button`
  width:100%;text-align:left;border:0;background:transparent;
  padding:10px 12px;border-radius:10px;font-weight:600;cursor:pointer;
  &:hover{ background:${COLORS.subtle}; }
`;

/* ================= APPLIED FILTERS ================= */
const AppliedFiltersBox = styled(Container)`
  padding-top:10px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;
`;
const FilterTag = styled.div`
  display:inline-flex;align-items:center;gap:8px;
  background:#f0f0f1;color:${COLORS.text};
  border:1px solid ${COLORS.line};border-radius:999px;
  padding:6px 10px;font-size:13px;
  .close{display:inline-flex;align-items:center;cursor:pointer;opacity:.7;}
  .close:hover{opacity:1;}
`;

/* ================= OVERLAY + DRAWER ================= */
const Overlay = styled.div`
  position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:3005;
  opacity:${({$open}) => $open ? 1 : 0};
  visibility:${({$open}) => $open ? 'visible' : 'hidden'};
  transition:.2s;
`;

const FiltersMenu = styled.aside`
  position:fixed;top:0;left:0;height:100dvh;width:86vw;max-width:360px;
  background:#fff;border-right:1px solid ${COLORS.line};
  transform: translateX(${({isVisible})=>isVisible?'0':'-100%'});
  transition: transform .25s ease; z-index:3006;
  display:grid;grid-template-rows:auto 1fr auto;overflow:hidden;
  -webkit-overflow-scrolling:touch;overscroll-behavior:contain;
`;

const FilterTop = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 16px;border-bottom:1px solid ${COLORS.line};
  h3{margin:0;font-size:16px;}
`;
const CloseButton = styled.button`
  background:#fff;border:1px solid ${COLORS.line};border-radius:10px;
  height:36px;width:36px;display:grid;place-items:center;cursor:pointer;
`;
const FilterBody = styled.div`
  padding:12px 14px 18px;display:grid;gap:14px;min-height:0;
  overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;
`;
const FilterSection = styled.div`border:1px solid ${COLORS.line};border-radius:12px;background:#fff;`;
const FilterHeaderBtn = styled.button`
  width:100%;border:0;background:#fff;padding:14px;
  display:flex;align-items:center;justify-content:space-between;
  font-weight:800;cursor:pointer;user-select:none;
`;

const FilterOptions = styled.div`
  display:flex;flex-direction:column;overflow:hidden;
  max-height:${({isExpanded,$tall})=>isExpanded?($tall?'68vh':'52vh'):'0'};
  pointer-events:${({isExpanded})=>isExpanded?'auto':'none'};
  transition:max-height .25s ease;

  > .content{position:sticky;top:0;padding:12px;background:#fff;z-index:1;border-bottom:1px solid rgba(0,0,0,.06);}
  > .scroll{
    flex:1;min-height:0;overflow-y:auto;padding:8px 12px 12px;
    -webkit-overflow-scrolling:touch;overscroll-behavior:contain;touch-action:pan-y;
    scrollbar-width:thin;
  }
  > .scroll::-webkit-scrollbar{width:8px;}
  > .scroll::-webkit-scrollbar-thumb{background:${COLORS.brand};border-radius:8px;}
  @media(max-width:768px){ max-height:${({isExpanded})=>isExpanded?'60vh':'0'}; }
`;

const SearchContainer = styled.div`
  display:flex;align-items:center;gap:8px;
  border:1px solid ${COLORS.line};border-radius:10px;padding:8px 10px;background:#fff;
  input{border:0;outline:0;flex:1;font-size:14px;}
  svg{color:${COLORS.dim};}
`;
const SearchInput = styled.input``;

const CheckboxList = styled.div`display:grid;gap:10px;`;
const CheckboxItem = styled.label`
  display:grid;grid-template-columns:22px 1fr;align-items:center;gap:10px;cursor:pointer;font-size:14px;color:${COLORS.text};
  input{display:none;}
  input + span{width:20px;height:20px;border:1px solid #cfcfcf;border-radius:4px;background:#fff;display:inline-block;position:relative;}
  input:checked + span{background:${COLORS.brand};border-color:${COLORS.brand};}
  input:checked + span::after{content:'';position:absolute;inset:4px;border:2px solid #fff;border-top:0;border-left:0;transform:rotate(45deg);}
`;

const PriceFilterContainer = styled.div`
  background:#fafafa;border:1px solid ${COLORS.line};border-radius:10px;padding:12px;display:grid;gap:12px;
`;
const PriceRangeWrapper = styled.div`display:grid;gap:6px;`;
const PriceLabel = styled.label`font-weight:800;font-size:14px;display:flex;justify-content:space-between;color:${COLORS.text};`;
const PriceRangeInput = styled.input`
  width:100%;height:6px;border-radius:999px;outline:none;-webkit-appearance:none;
  background:linear-gradient(to right, ${COLORS.brand} 0%, #e7c2c2 100%);
  &::-webkit-slider-thumb{appearance:none;width:18px;height:18px;border-radius:50%;background:${COLORS.brand};cursor:pointer;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.2);}
`;
const DrawerFooter = styled.div`border-top:1px solid ${COLORS.line};padding:12px;display:grid;gap:8px;`;
const ClearFiltersButton = styled.button`
  width:100%;height:44px;border-radius:12px;border:1px solid ${COLORS.line};
  background:${COLORS.text};color:#fff;font-weight:800;cursor:pointer;
  transition:filter .15s ease, transform .15s ease;
  &:hover{ filter:brightness(.95); transform:translateY(-1px); }
`;

/* ================= GRID / CARDS ================= */
const PerfumeGrid = styled.div`
  display:grid;gap:16px;
  grid-template-columns:repeat(5,minmax(0,1fr));
  @media (max-width:1200px){grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;}
  @media (max-width:900px){grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}
  @media (max-width:640px){grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}
  @media (max-width:380px){grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
`;

const PerfumeCard = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  padding: 12px;
  position: relative;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  cursor: pointer;
  min-width: 0;
  box-sizing: border-box;

  &:hover { box-shadow: ${COLORS.shadow}; transform: translateY(-2px); border-color: rgba(0,0,0,.12); }

  .hover-options {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; z-index: 2; border-radius: 16px;
  }

  .view-btn{
    border: 1px solid ${COLORS.line}; background:#fff; border-radius: 12px; height: 38px; padding: 0 12px; font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease; color: black;
  }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }
  
  @media (max-width: 640px) {
    padding: 8px; border-radius: 12px;
  }
  @media (max-width: 380px) {
    padding: 6px; border-radius: 10px;
  }
`;

const ImgWrap = styled.div`
  width:100%;aspect-ratio:1/1;border-radius:12px;background:${COLORS.subtle};
  display:grid;place-items:center;overflow:hidden;margin-bottom:10px;position:relative;
  @media (max-width:640px){ margin-bottom:6px; }
`;
const PerfumeImage = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
  object-position: center;
  display: block;
  @media (max-width: 640px) { width: 80%; height: 80%; }
`;

const PerfumeName = styled.h3`
  font-size:14px;font-weight:800;color:${COLORS.text};
  margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  @media(max-width:640px){font-size:13px;}
`;
const PerfumeBrand = styled.div`font-size:12px;color:${COLORS.dim};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;

const PriceBlock = styled.div`
  margin-top:8px;font-weight:800;color:${COLORS.text};font-size:13px;
  .old{ color:#9ca3af; text-decoration:line-through; margin-right:6px; }
  @media(max-width:640px){font-size:12px;}
`;

const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;

  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;

const IconBar = styled.div`
  position:absolute;left:10px;top:10px;display:flex;gap:8px;z-index:3;
  .icon-btn{
    width:36px;height:36px;border-radius:10px;border:1px solid ${COLORS.line};
    background:#fff;display:grid;place-items:center;cursor:pointer;
    transition:transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover{ transform:translateY(-1px); box-shadow:${COLORS.shadow}; }
`;

const CardLink = styled(Link)`text-decoration:none;color:inherit;`;
const CardBody = styled.div`text-decoration:none;color:inherit;`;

/* ================= BRAND HERO ================= */
const BackgroundSection = styled.div`
  width:100%;display:flex;justify-content:center;align-items:center;text-align:center;
  overflow:hidden;max-height:70vh;border-bottom:1px solid ${COLORS.line};background:#fff;
`;
const StyledImage = styled.img`
  width:100%;height:auto;display:block;
  @media(max-width:900px){ height:50vh; object-fit:cover; }
`;
const BrandIntro = styled.div`
  max-width:980px;margin:18px auto 28px;padding:0 8px;
  h3{margin:0 0 10px;font-size:18px;font-weight:900;color:${COLORS.text};}
  p{margin:0;color:${COLORS.dim};line-height:1.6;white-space:pre-wrap;}
`;

const LoadMoreButton = styled.button`
  display: block; margin: 24px auto 40px; height: 44px; padding: 0 16px;
  background: ${COLORS.text}; color:#fff; border: 0; border-radius: 12px; font-weight: 800; cursor:pointer;
  transition: transform .15s ease, filter .15s ease;
  &:hover{ transform: translateY(-1px); filter: brightness(.95); }
`;

/* ================= BRAND BACKGROUNDS MAP ================= */
const brandBackgrounds = {
  "orto parisi": "/images/orto-parisi-bg.jpg",
  "creed": "/images/creed-bg.jpg",
  "tom ford": "/images/tom-ford-bg.jpg",
  "dior": "/images/dior-bg.jpg",
  "chanel": "/images/chanel-bg.jpg",
  "acqua di parma": "/images/acqua-di-parma-bg.jpg",
  "afnan": "/images/afnan-bg.webp",
  "al haramain": "/images/al-haramain-bg.jpg",
  "amouage": "/images/amouage-bg.webp",
  "argos": "/images/argos-bg.jpg",
  "armaf": "/images/armaf-bg.webp",
  "azzaro": "/images/azzaro-bg.jpg",
  "bdk parfums": "/images/bdk-parfums-bg.jpeg",
  "boadicea the victorious": "/images/Boadicea-the-Victorious-bg.jpg",
  "bvlgari": "/images/Bvlgari-bg.jpg",
  "by kilian": "/images/By-Kilian-bg.jpg",
  "byredo": "/images/byredo-bg.jpg",
  "carolina herrera": "/images/carolina-Herrera-bg.jpg",
  "clive christian": "/images/clive-christian-bg.jpg",
  "dolce & gabbana": "/images/dolce-gabana-bg.jpg",
  "escentric molecules": "/images/escentric-molecules-bg.jpg",
  "essential parfums": "/images/essential-parfums-bg.jpg",
  "ex nihilo": "/images/ex-nihilo-bg.jpg",
  "frederic malle": "/images/frederic-malle-bg.jpg",
  "giorgio armani": "/images/giorgio-armani-bg.jpg",
  "givenchy": "/images/givenchy-bg.avif",
  "goldfield & banks": "/images/goldfield-&-banks-bg.webp",
  "gritti": "/images/gritti-bg.jpg",
  "hermès": "/images/hermès-bg.jpg",
  "histoires de parfums": "/images/histoires-de-parfums-bg.webp",
  "initio": "/images/initio-bg.webp",
  "jean paul gaultier": "/images/jean-paul-gaultier-bg.jpg",
  "jo malone": "/images/jo-malone-bg.jpg",
  "juliette has a gun": "/images/juliette-has-a-gun.webp",
};

/* ================= HELPERS (default ML & payload) ================= */
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
// ================= HELPERS (same as slider) =================
// იგივე ლოგიკა, რაც App.js-ში გაქვს
// ─────────────────────────────────────────────
// STOCK STRATEGY (იგივე, რაც App.js-ში გაქვს)
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// STOCK STRATEGY (იგივე)
// ─────────────────────────────────────────────
function mlStrategy(ml, item) {
  if (ml?.stock_strategy === "units") return "units";
  if (ml?.stock_strategy === "full")  return "full";
  // fallback legacy:
  return item?.per_ml_stock && ml?.unit_stock != null ? "units" : "full";
}

// ─────────────────────────────────────────────
// Helper: აქვს საერთოდ მარაგი?
// FULL → pooled full_stock უნდა იყოს ≥ size და >0
// UNITS → unit_stock > 0
// ─────────────────────────────────────────────
function hasAnyStock(item) {
  const mls = Array.isArray(item?.milliliters) ? item.milliliters : [];
  if (!mls.length) return false;

  const fullCap = Number(item?.full_stock ?? 0);

  const hasFull = mls.some(ml => {
    const strat = mlStrategy(ml, item);
    const size  = Number(ml?.size ?? 0);
    return strat === "full" && fullCap > 0 && size > 0 && fullCap >= size;
  });

  const hasUnits = mls.some(ml => {
    const strat = mlStrategy(ml, item);
    const units = Number(ml?.unit_stock ?? 0);
    return strat === "units" && units > 0;
  });

  return hasFull || hasUnits;
}

// ─────────────────────────────────────────────
// STRICT picker (არანაირი fallback!)
// 1) FULL → ყველაზე პატარა ზომა, რომელსაც pooled full_stock ჰყოფნის
// 2) UNITS → unit_stock>0-იანი, ყველაზე იაფი
// ვერ მოიძებნა? → null
// ─────────────────────────────────────────────
function pickDefaultMlForQuickAdd(item) {
  const mls = Array.isArray(item?.milliliters) ? item.milliliters : [];
  if (!mls.length) return null;

  const fullCap = Number(item.full_stock ?? 0);
  const withStrat = mls
    .filter(m => Number(m.size) > 0)
    .map(m => ({ ...m, _strategy: mlStrategy(m, item) }));

  const fullPourables = withStrat
    .filter(m => m._strategy === "full" && fullCap > 0 && fullCap >= Number(m.size))
    .sort((a,b) => Number(a.size) - Number(b.size));
  if (fullPourables.length) return fullPourables[0];

  const unitables = withStrat
    .filter(m => m._strategy === "units" && Number(m.unit_stock ?? 0) > 0)
    .sort((a,b) => Number(a.sale_price ?? a.price) - Number(b.sale_price ?? b.price));
  if (unitables.length) return unitables[0];

  // ❌ საერთოდ არ არის მარაგი — ვაბრუნებთ null-ს
  return null;
}

/* ================= COMPONENT ================= */
const BrandPage = ({ addToCart }) => {
  const { brand } = useParams();
  const [perfumes, setPerfumes] = useState([]);
  const [filteredPerfumes, setFilteredPerfumes] = useState([]);
  const [aromas, setAromas] = useState([]);
  const [selectedAromas, setSelectedAromas] = useState([]);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [aromaExpanded, setAromaExpanded] = useState(false);
  const [genderExpanded, setGenderExpanded] = useState(false);
  const [brandDetails, setBrandDetails] = useState(null);
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);
  const [sortOrder, setSortOrder] = useState('');
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryExpanded, setCategoryExpanded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [visiblePerfumes, setVisiblePerfumes] = useState(40);

  // hover overlay toggle
  const [activeCard, setActiveCard] = useState(null);

  const [aromaSearch, setAromaSearch] = useState('');
  const { t, language } = useLanguage();

  const dropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
  const normalizedBrand = brand.replace(/-/g, " ").toLowerCase();
  const location = useLocation();

  useEffect(() => { document.title = t("titles.brandspage"); }, [language]);

  // click outside – drawer & sort & card overlay
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setFiltersVisible(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSortDropdownVisible(false);
      }
      const insideCard = e.target.closest('[data-card="true"]');
      if (!insideCard) setActiveCard(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchPerfumes = async () => {
      setLoading(true);
      try {
        const perfumeResponse = await api.get(`${API_BASE_URL}/get_perfumes.php`);
        const perfumesData = perfumeResponse.data || [];
        const filtered = perfumesData.filter((p) => (p.brand || '').toLowerCase() === normalizedBrand);

        setPerfumes(filtered);
        setFilteredPerfumes(filtered);

        const brandResponse = await api.get(`${API_BASE_URL}/get_brands.php`);
        const brandsData = brandResponse.data || [];
        const selectedBrand = brandsData.find((b) => (b.name || '').toLowerCase() === normalizedBrand);
        setBrandDetails(selectedBrand);

        const allPrices = filtered.flatMap((p) =>
          p.milliliters ? p.milliliters.map((ml) => parseFloat(ml.price)) : []
        );
        if (allPrices.length) {
          const min = Math.min(...allPrices);
          const max = Math.max(...allPrices);
          setMinPrice(min); setMaxPrice(max); setPriceRange([min, max]);
        }

        setAromas([
          ...new Set(
            filtered.flatMap((p) => p.aroma_en ? p.aroma_en.split(",").map(a => a.trim()) : [])
          ),
        ]);

        const cats = [...new Set(filtered.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      } catch (e) {
        console.error("❌ Error fetching perfumes:", e);
      } finally {
        setTimeout(() => setLoading(false), 300);
      }
    };
    fetchPerfumes();
  }, [normalizedBrand, language, location.pathname]);

  // derive aromas from filtered list
  useEffect(() => {
    if (!perfumes.length) return;
    setAromas([
      ...new Set(
        filteredPerfumes.flatMap((p) =>
          p.aroma_en ? p.aroma_en.split(",").map((a) => a.trim()) : []
        )
      ),
    ]);
  }, [language, perfumes, filteredPerfumes]);

  // lock body scroll when drawer open
  useEffect(() => {
    if (!filtersVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [filtersVisible]);

  const getBrandImage = (bd) => bd?.image_url || bd?.image || bd?.logo_url || "";
  const getBrandBackground = (nb, bd) =>
    brandBackgrounds[nb] || bd?.background_url || getBrandImage(bd) || "/images/default-brand-bg.jpg";

  const toggleFiltersVisibility = () => setFiltersVisible(p => !p);
  const toggleSection = (setter, val) => setter(!val);

  const applyFilters = () => {
    let filtered = perfumes;

    if (isPriceFilterActive) {
      filtered = filtered.filter((p) =>
        p.milliliters?.some(
          (ml) => parseFloat(ml.price) >= priceRange[0] && parseFloat(ml.price) <= priceRange[1]
        )
      );
    }
    if (selectedCategories.length) filtered = filtered.filter(p => selectedCategories.includes(p.category));
    if (selectedGenders.length)   filtered = filtered.filter(p => selectedGenders.includes(p.sex));
    if (selectedAromas.length) {
      filtered = filtered.filter((p) =>
        selectedAromas.some((a) => p.aroma_en && p.aroma_en.includes(a))
      );
    }

    filtered = filtered.map((p) => ({
      ...p,
      sortedPrices: p.milliliters
        ? p.milliliters.map(ml => parseFloat(ml.price)).filter(n => n > 0).sort((a,b) => a-b)
        : [],
    }));

    if (sortOrder) {
      filtered = [...filtered].sort((a,b) =>
        sortOrder === 'asc'
          ? (a.sortedPrices[0] || 0) - (b.sortedPrices[0] || 0)
          : (b.sortedPrices[0] || 0) - (a.sortedPrices[0] || 0)
      );
    }

    setFilteredPerfumes(filtered);

    const chips = [
      ...selectedCategories.map(v => ({type:'category', value:v})),
      ...selectedAromas.map(v => ({type:'aroma', value:v})),
      sortOrder && {type:'sort', value: sortOrder === 'asc' ? 'Min to Max' : 'Max to Min'},
      isPriceFilterActive && {type:'price', value: `${priceRange[0]} - ${priceRange[1]} ${t("newproducts.currency")}`},
      ...selectedGenders.map(v => ({type:'gender', value:v})),
    ].filter(Boolean);
    setAppliedFilters(chips);
  };

  useEffect(() => {
    applyFilters();
  }, [selectedCategories, selectedAromas, priceRange, sortOrder, isPriceFilterActive, selectedGenders]);

  const removeFilter = (filter) => {
    if (filter.type === "aroma") setSelectedAromas(arr => arr.filter(a => a !== filter.value));
    else if (filter.type === "price"){ setIsPriceFilterActive(false); setPriceRange([minPrice, maxPrice]); }
    else if (filter.type === "sort") setSortOrder("");
    else if (filter.type === "gender") setSelectedGenders(arr => arr.filter(g => g !== filter.value));
    else if (filter.type === "category") setSelectedCategories(arr => arr.filter(c => c !== filter.value));

    setAppliedFilters(prev => prev.filter(f => !(f.type === filter.type && f.value === filter.value)));
  };

  const clearFilters = () => {
    setSelectedAromas([]); setSortOrder(''); setAppliedFilters([]);
    setIsPriceFilterActive(false); setPriceRange([minPrice, maxPrice]);
    setFilteredPerfumes(perfumes); setSelectedGenders([]); setSelectedCategories([]);
  };

  const handlePriceChange = (e) => {
    const value = Number(e.target.value);
    setIsPriceFilterActive(true);
    if (e.target.name === "min") setPriceRange(([_, max]) => [value, max]);
    else setPriceRange(([min, _]) => [min, value]);
  };

  const handleCardClick = (e, id) => {
    e.stopPropagation();
    setActiveCard(prev => (prev === id ? null : id));
  };

  const anySaleOnCard = (p) =>
    p.milliliters?.some((ml) => ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price));

  // ✅ ADD TO CART WRAPPER — perfume: pick default ml & qty=1
// ✅ perfume-სთვის ავიღოთ default ML და qty=1 — ზუსტად სლაიდერის მსგავსად
const handleAddToCart = (e, p) => {
  e.stopPropagation();
  const picked = pickDefaultMlForQuickAdd(p);
  if (!picked) return; // no stock → არაფერი ვაკეთებთ

  const sizeNum = Number(picked.size);

  // 🔑 ზუსტად იმ ფორმატში, რასაც App.addToCart კითხულობს:
  // - type: "perfume" (რომ აქციოს perfume-ად და არა accessory-ად)
  // - category დარჩეს p.category-დან
  // - selected_ml [{ size, quantity:1 }] — რომ CartOverlay-საც ეყოს
  // - quantities { [size]: 1 } — extractPerfumeAdditions-საც ეყოს
  const payload = {
    ...p,
    type: "perfume",
    category: p.category,                 // არ დავკარგოთ კატგორია
    image: (p.imageUrls?.[0]) || p.image || "",
    selected_ml: [{ size: sizeNum, quantity: 1 }],
    quantities: { [String(sizeNum)]: 1 },
    quantity: 1,
  };

  addToCart(payload);
};

  if (loading) return <PageLoader><Spinner/></PageLoader>;

  return (
    <>
      {/* HERO */}
      <BackgroundSection>
        <StyledImage src={getBrandBackground(normalizedBrand, brandDetails)} alt="Brand Background" />
      </BackgroundSection>

      {/* ABOUT */}
      <Container>
        <BrandIntro>
          <h3>{t("brandpage.about")}</h3>
          {brandDetails ? <p>{brandDetails[t("brandpage.description")]}</p> : <p>Loading...</p>}
        </BrandIntro>
      </Container>

      {/* STICKY CONTROLS */}
      <TopBar>
        <TopBarInner>
          <LeftActions>
            <PillButton onClick={() => setFiltersVisible(p => !p)}><FaFilter/>{t("categorypage.filter")}</PillButton>
          </LeftActions>

          <RightActions ref={dropdownRef}>
            <SortDropdownWrap>
              <PillButton onClick={() => setSortDropdownVisible(p => !p)}><FaSort/>{t("categorypage.sort")}</PillButton>
              <DropdownCard $hidden={!sortDropdownVisible}>
                <DropdownItem onClick={() => { setSortOrder('asc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.mintomax")}
                </DropdownItem>
                <DropdownItem onClick={() => { setSortOrder('desc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.maxtomin")}
                </DropdownItem>
              </DropdownCard>
            </SortDropdownWrap>
          </RightActions>
        </TopBarInner>

        <AppliedFiltersBox>
          {appliedFilters.length ? (
            appliedFilters.map((filter, i) => (
              <FilterTag key={`${filter.type}-${filter.value}-${i}`}>
                {filter.type === "aroma" ? `${t("categorypage.aroma")}:`
                 : filter.type === "price" ? `${t("categorypage.price")}:`
                 : filter.type === "sort" ? `${t("categorypage.sort-filter")}:`
                 : filter.type === "gender" ? `${t("categorypage.gender")}:`
                 : filter.type === "category" ? `${t("categorypage.categories")}:`
                 : filter.type}
                &nbsp;{filter.type === "gender" ? t(`categorypage.${filter.value}`) : (filter.value ?? `${priceRange[0]} - ${priceRange[1]} ${t("newproducts.currency")}`)}
                <span className="close" onClick={() => removeFilter(filter)}><FaTimes/></span>
              </FilterTag>
            ))
          ) : (
            <span style={{ color: COLORS.dim, fontSize: 13 }}>{t("categorypage.filteroff")}</span>
          )}
        </AppliedFiltersBox>
      </TopBar>

      {/* OVERLAY + DRAWER */}
      <Overlay $open={filtersVisible} onClick={() => setFiltersVisible(false)} />
      <FiltersMenu isVisible={filtersVisible} ref={filterMenuRef}>
        <FilterTop>
          <h3>{t("categorypage.filter")}</h3>
          <CloseButton onClick={() => setFiltersVisible(false)} aria-label="Close"><FaTimes/></CloseButton>
        </FilterTop>

        <FilterBody>
          {!!categories.length && (
            <FilterSection>
              <FilterHeaderBtn onClick={() => setCategoryExpanded(v => !v)}>
                <span>{t("categorypage.categories")}</span>
                {categoryExpanded ? <FaChevronUp/> : <FaChevronDown/>}
              </FilterHeaderBtn>
              <FilterOptions isExpanded={categoryExpanded}>
                <div className="scroll">
                  {categories.map((cat) => (
                    <CheckboxItem key={cat}>
                      <input
                        type="checkbox"
                        value={cat}
                        checked={selectedCategories.includes(cat)}
                        onChange={(e) =>
                          setSelectedCategories(prev => e.target.checked ? [...prev, cat] : prev.filter(c => c !== cat))
                        }
                      />
                      <span></span>{t(`categorypage.${cat}`)}
                    </CheckboxItem>
                  ))}
                </div>
              </FilterOptions>
            </FilterSection>
          )}

          <FilterSection>
            <FilterHeaderBtn onClick={() => setGenderExpanded(v => !v)}>
              <span>{t("categorypage.gender")}</span>
              {genderExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={genderExpanded}>
              <div className="scroll">
                {["man", "woman", "kids", "unisex"].map((g) => (
                  <CheckboxItem key={g}>
                    <input
                      type="checkbox"
                      value={g}
                      checked={selectedGenders.includes(g)}
                      onChange={(e) =>
                        setSelectedGenders(prev => e.target.checked ? [...prev, g] : prev.filter(x => x !== g))
                      }
                    />
                    <span></span>{t(`categorypage.${g}`)}
                  </CheckboxItem>
                ))}
              </div>
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterHeaderBtn onClick={() => setAromaExpanded(v => !v)}>
              <span>{t("categorypage.aroma")}</span>
              {aromaExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={aromaExpanded} $tall>
              <div className="content">
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder={t("categorypage.searcharoma")}
                    onChange={(e) => setAromaSearch(e.target.value)}
                    value={aromaSearch}
                  />
                  <FaSearch/>
                </SearchContainer>
              </div>
              <div className="scroll">
                <CheckboxList>
                  {aromas
                    .filter((a) => a.toLowerCase().includes(aromaSearch.toLowerCase()))
                    .map((aroma) => (
                      <CheckboxItem key={aroma}>
                        <input
                          type="checkbox"
                          value={aroma}
                          checked={selectedAromas.includes(aroma)}
                          onChange={(e) =>
                            setSelectedAromas(prev => e.target.checked ? [...prev, aroma] : prev.filter(x => x !== aroma))
                          }
                        />
                        <span></span>{aroma}
                      </CheckboxItem>
                    ))}
                </CheckboxList>
              </div>
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterHeaderBtn as="div"><span>{t("categorypage.price")}</span></FilterHeaderBtn>
            <div style={{padding:12}}>
              <PriceFilterContainer>
                <PriceRangeWrapper>
                  <PriceLabel>{t("categorypage.minprice")} <b>{priceRange[0]} {t("newproducts.currency")}</b></PriceLabel>
                  <PriceRangeInput type="range" name="min" min={minPrice} max={maxPrice || 0} value={priceRange[0]} onChange={handlePriceChange}/>
                </PriceRangeWrapper>
                <PriceRangeWrapper>
                  <PriceLabel>{t("categorypage.maxprice")} <b>{priceRange[1]} {t("newproducts.currency")}</b></PriceLabel>
                  <PriceRangeInput type="range" name="max" min={minPrice} max={maxPrice || 0} value={priceRange[1]} onChange={handlePriceChange}/>
                </PriceRangeWrapper>
              </PriceFilterContainer>
            </div>
          </FilterSection>
        </FilterBody>

        <DrawerFooter>
          <ClearFiltersButton onClick={clearFilters}>{t("categorypage.clearfilters")}</ClearFiltersButton>
        </DrawerFooter>
      </FiltersMenu>

      {/* GRID */}
      <GridWrap>
        <PerfumeGrid>
          {filteredPerfumes.slice(0, visiblePerfumes).map((p) => {
            const isMlBasedCard = ["perfume","skin-care","hair-and-body-care"].includes(p.category);
            const minMlSize =
              isMlBasedCard && Array.isArray(p.milliliters) && p.milliliters.length
                ? Math.min(...p.milliliters.map(ml => parseFloat(ml.size)).filter(n => !isNaN(n) && n > 0))
                : 0;

const candidate = pickDefaultMlForQuickAdd(p);
const showAddToCart = !!candidate; // თუ მარაგი არაა, ღილაკი აღარ გამოჩნდება

            const originalPrices = p.milliliters?.map(ml => parseFloat(ml.price)) || [];
            const salePrices     = p.milliliters?.map(ml => parseFloat(ml.sale_price) || parseFloat(ml.price)) || [];
            const minOriginal = originalPrices.length ? Math.min(...originalPrices) : 0;
            const maxOriginal = originalPrices.length ? Math.max(...originalPrices) : 0;
            const minSale = salePrices.length ? Math.min(...salePrices) : 0;
            const maxSale = salePrices.length ? Math.max(...salePrices) : 0;
            const priceChanged = (minSale < minOriginal) || (maxSale < maxOriginal);

            return (
              <PerfumeCard
                key={p.product_id}
                data-card="true"
                onClick={(e)=>handleCardClick(e, p.product_id)}
                $active={activeCard === p.product_id}
              >
                {anySaleOnCard(p) && <SaleBadge>{t("newproducts.sale")}</SaleBadge>}

                <div className="hover-options" onClick={(e)=>{ e.stopPropagation(); setActiveCard(null); }}>
                  <CardLink 
                    to={`/product/${p.product_id}`}
                    onMouseDown={(e)=>e.stopPropagation()}
                    onClick={(e)=>e.stopPropagation()}
                  >
                    <button className="view-btn">{t("categorypage.viewmore")}</button>
                  </CardLink>
                </div>

                <CardBody>
                  <ImgWrap>
                    <PerfumeImage src={p.imageUrls?.[0]} alt={p.name}/>
                  </ImgWrap>

                  <PerfumeName title={p.name}>{p.name}</PerfumeName>
                  <PerfumeBrand title={p.brand}>{p.brand}</PerfumeBrand>

                  <PriceBlock>
                    {p.milliliters?.length ? (
                      priceChanged ? (
                        <>
                          <span className="old">
                            {minOriginal === maxOriginal
                              ? `${minOriginal} ${t("newproducts.currency")}`
                              : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
                          </span>
                          <span>
                            {minSale === maxSale
                              ? `${minSale} ${t("newproducts.currency")}`
                              : `${minSale} - ${maxSale} ${t("newproducts.currency")}`}
                          </span>
                        </>
                      ) : (
                        <span>
                          {minOriginal === maxOriginal
                            ? `${minOriginal} ${t("newproducts.currency")}`
                            : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
                        </span>
                      )
                    ) : "N/A"}
                  </PriceBlock>
                </CardBody>

                <IconBar onClick={(e)=>e.stopPropagation()}>
                  {showAddToCart && (
                    <button
                      className="icon-btn"
                      onClick={(e)=>handleAddToCart(e, p)}
                      aria-label={t("categorypage.addtocart") || "Add to cart"}
                    >
                      <ShoppingCart size={18}/>
                    </button>
                  )}
                  <div className="icon-btn">
                    <FavoriteButton productId={p.product_id} initialIsFavorite={p.is_favorite}/>
                  </div>
                </IconBar>
              </PerfumeCard>
            );
          })}
        </PerfumeGrid>

        {visiblePerfumes < filteredPerfumes.length && (
          <LoadMoreButton onClick={()=>setVisiblePerfumes(v => v + 8)}>
            {t("purchases.loadmore")}
          </LoadMoreButton>
        )}
      </GridWrap>
    </>
  );
};

export default BrandPage;
